import decodeJwt from 'jwt-decode';
import { fetchUtils, addRefreshAuthToAuthProvider } from 'react-admin';
import config from '../config';
import {
  authTokenStore,
  clockDriftStore,
  lastAuthenticatedActivityStore,
  mfaChallengeStore,
  refreshTokenStore,
} from './authTokenStore';
import hasClockDrift from './hasClockDrift';
import { Actions } from '../constants/actions';
import authRoutes from '../constants/authRoutes';
import errorLogger from './errorLogger';
import { errorCodes } from '../constants/errorMessages';

const httpClient = fetchUtils.fetchJson;

export type Permission = Actions | { name: Actions; attributes: unknown };

type AuthToken = {
  id?: string;
  username?: string;
  displayName?: string;
  accountName?: string;
  accountId?: string;
  actions?: Permission[];
  iat: number;
  exp: number;
}

type RefreshToken = {
  id?: string;
  accountId?: string;
  iat: number;
  exp: number;
}

type CredentialsLoginParams = {
  username: string;
  password: string;
  alias: string;
}

type MfaLoginParams = {
  challengeToken: string;
  totpCode?: string;
  recoveryCode?: string;
}

type Session = {
  token: string;
  refreshToken?: string;
}

type LoginParams = CredentialsLoginParams | MfaLoginParams | Session;

const isSessionLogin = (params: LoginParams): params is Session => 'token' in params;

const isMfaLogin = (params: LoginParams): params is MfaLoginParams => 'challengeToken' in params;

const startSession = ({ token, refreshToken }: Session) => {
  mfaChallengeStore.removeToken();
  authTokenStore.setToken(token);
  if (refreshToken) refreshTokenStore.setToken(refreshToken);
};

export const refreshAuth = (() => {
  let ongoingRefresh: Promise<void> | null = null;

  return async (minimumValidityMs = 5000) => {
    const authToken = authTokenStore.getToken();
    const refreshToken = refreshTokenStore.getToken();

    if (authToken && refreshToken) {
      const decodedAuthToken = decodeJwt<AuthToken>(authToken);
      const decodedRefreshToken = decodeJwt<RefreshToken>(refreshToken);
      const isAuthExpired = (decodedAuthToken.exp * 1000 - Date.now()) <= minimumValidityMs;
      const isRefreshExpired = (decodedRefreshToken.exp * 1000 - Date.now()) <= 5000;

      if (isAuthExpired && !isRefreshExpired) {
        if (ongoingRefresh) return ongoingRefresh;

        ongoingRefresh = (async () => {
          try {
            const response = await httpClient(`${config.apiUrl}/auth/refresh-token`, {
              method: 'POST',
              body: JSON.stringify({ refreshToken }),
            });
            const newAuthToken = response.json.token;
            if (newAuthToken) authTokenStore.setToken(newAuthToken);
          } catch (e) {
            // Continue even if the refresh failed
            errorLogger(e);
          } finally {
            ongoingRefresh = null;
          }
        })();
        return ongoingRefresh;
      }
    }
    return undefined;
  };
})();

const baseAuthProvider = {
  login: async (login: LoginParams) => {
    if (isSessionLogin(login)) {
      startSession(login);
      return undefined;
    }

    if (isMfaLogin(login)) {
      const response = await httpClient(`${config.apiUrl}/auth/mfa/verify`, {
        method: 'POST',
        body: JSON.stringify(login),
      });
      const verified = response.json;
      if (!verified) throw new Error('Network error');
      if (hasClockDrift(verified)) clockDriftStore.setToken(String(verified.clockDriftSteps));
      else clockDriftStore.removeToken();
      startSession(verified);
      return undefined;
    }

    const response = await httpClient(`${config.apiUrl}/auth/login`, {
      method: 'POST',
      body: JSON.stringify(login),
    });
    const auth = response.json;
    if (!auth) throw new Error('Network error');

    if (auth.mfaRequired) {
      mfaChallengeStore.setToken(auth.challengeToken);
      return { redirectTo: authRoutes.MFA_CHALLENGE };
    }

    if (auth.mfaEnrollmentRequired) {
      mfaChallengeStore.setToken(auth.challengeToken);
      return { redirectTo: authRoutes.MFA_FORCED_ENROLLMENT };
    }

    startSession(auth);
    return undefined;
  },
  checkError: async (error: { message: string; status: number; body: unknown }) => {
    const { status } = error;
    const checkToken = authTokenStore.getToken();
    if (status === 401 && checkToken) {
      authTokenStore.removeToken();
      refreshTokenStore.removeToken();
      lastAuthenticatedActivityStore.removeToken();
      mfaChallengeStore.removeToken();
      throw new Error('Authentication error');
    }
  },
  checkAuth: async () => {
    const checkToken = authTokenStore.getToken();
    // eslint-disable-next-line prefer-promise-reject-errors
    if (!checkToken) return Promise.reject({ message: false });
    return Promise.resolve();
  },
  logout: async () => {
    await refreshAuth();
    const authToken = authTokenStore.getToken();
    try {
      if (authToken) {
        const logoutHeaders = new Headers({
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        }) as Headers;
        await httpClient(`${config.apiUrl}/auth/logout`, {
          method: 'POST',
          headers: logoutHeaders,
          body: JSON.stringify({ token: authToken }),
        });
      }
    } catch (e) {
      /* do nothing */
    }
    authTokenStore.removeToken();
    refreshTokenStore.removeToken();
    lastAuthenticatedActivityStore.removeToken();
    mfaChallengeStore.removeToken();
    clockDriftStore.removeToken();
  },
  getIdentity: async () => {
    const token = authTokenStore.getToken();
    if (!token) throw new Error('Authentication error');
    const {
      id,
      username,
      displayName,
      accountName,
      accountId,
    } = decodeJwt<AuthToken>(token);
    if (!id) throw new Error('Authentication error');
    return {
      id,
      displayName,
      username,
      avatar: undefined,
      accountName,
      accountId,
    };
  },
  getPermissions: async () => {
    const token = authTokenStore.getToken();
    if (!token) return [];
    const { actions } = decodeJwt<AuthToken>(token);
    return actions ?? [];
  },
  handleCallback: async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get('redirectTo');
    const errorPageQueryParams = new URLSearchParams();
    if (redirectTo) errorPageQueryParams.append('redirectTo', redirectTo);

    const errorCode = urlParams.get('errorCode');
    if (errorCode) {
      errorPageQueryParams.append('errorCode', errorCode);
      return { redirectTo: `/login?${errorPageQueryParams.toString()}` };
    }

    const token = urlParams.get('token');
    if (!token || !token.length) {
      errorPageQueryParams.append('errorCode', errorCodes.SSO_INVALID_TOKEN);
      return { redirectTo: `/login?${errorPageQueryParams.toString()}` };
    }

    startSession({ token, refreshToken: urlParams.get('refreshToken') ?? undefined });

    if (redirectTo) return { redirectTo };

    return undefined;
  },
};

const authProvider = addRefreshAuthToAuthProvider(baseAuthProvider, refreshAuth);

export default authProvider;
